import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import Toast from '../../Toast';
import { BulkUploadAddQuestionStep } from './BulkUploadAddQuestionStep';
import { BulkUploadDragDropStep } from './BulkUploadDragDropStep';
import { BulkUploadConvertingStep } from './BulkUploadConvertingStep';
import { BulkUploadOrganisePagesStep } from './BulkUploadOrganisePagesStep';
import { BulkUploadReviewStep } from './BulkUploadReviewStep';
import { BulkUploadMarkingRubricsStep } from './BulkUploadSubmitStep';
import PopupDialogueBox from '../../../components2/PopupDialogueBox';
import TopBannerBox from '../../../components2/TopBannerBox';
import GeneralPopup from '../../GeneralPopup';
import warningIcon from '../../../static/icons/Warning-icon.svg';
import { useUnsavedWorkGuard } from '../../../hooks/useUnsavedWorkGuard';
import { DraftSubmissionsView } from './DraftSubmissionsView';
import { FlowPage, ProgressAction } from '../styles';
import { FeedbackGenerationProgress } from '../../Shared/FeedbackGenerationProgress';
import { ShareOrDownloadControl } from '../../ShareOrDownloadControl';
import {
  flattenClassStudents,
  mergeStudentRecords,
  excludeStudentsWithSubmissions,
} from '../utils/students';
import { useActiveClassData, useAssignmentById } from '../../state/hooks';
import { useBulkUploadSubmission } from '../../../hooks/useBulkUploadSubmission';
import {
  createAssignment,
  getSubmissionsByAssignmentId,
  newIdempotencyKey,
} from '../../../service';
import { hasReviewEdits, responseTextOf } from '../utils/reviewEdits';
import { answerableQuestionsOf, isMultiQuestionAssignment } from '../utils/questionSplit';
import { buildDraftAssignmentPayload } from '../utils/assignmentPayload';
import { runRefusalMessage, bulkFeedbackPhase } from '../utils/feedbackRun';
import { saveDraftQuestion } from '../utils/saveDraftQuestion';
import { createSubmissionsFor } from '../utils/draftSubmissions';
import { isFinalBatchRejection } from '../utils/pendingBatch';
import { submissionBatchErrorMessage } from '../utils/submissionBatchErrors';
import { UNTITLED_DRAFT_TITLE } from '../../../utils/draftTitle';
import { endWithFullStop } from '../../../utils/strings';
import {
  useDraftPageSync,
  DraftPageSyncStatus,
} from '../../../hooks/useDraftPageSync';
import { useResumedThumbnails } from '../../../hooks/useResumedThumbnails';
import { useAssignmentExtractionRun } from '../../../hooks/useAssignmentExtractionRun';
import { mapSubmissionsOntoPages } from '../utils/submissionExtraction';
import { isMobileExperienceFlagOn } from '../../../components-mobile/useMobileExperience';

const BULK_UPLOAD_WIZARD_STEPS = [
  { id: 1, label: 'Add question' },
  { id: 2, label: 'Add responses' },
  { id: 3, label: 'Organise pages' },
  { id: 4, label: 'Review' },
  { id: 5, label: 'Marking rubrics' },
];

const INDEPENDENT_TEACHER_BULK_UPLOAD_STEPS = [
  { id: 1, label: 'Add question' },
  { id: 2, label: 'Add responses' },
  { id: 3, label: 'Organise pages' },
  { id: 4, label: 'Review' },
  { id: 5, label: 'Marking rubrics' },
];

const TASKS_BULK_UPLOAD_STEPS = [
  { id: 1, label: 'Add responses' },
  { id: 2, label: 'Organise pages' },
  { id: 3, label: 'Review' },
];

const STEPS = {
  ENTRY: 'entry',
  QUESTION: 'question',
  DRAG_DROP: 'dragDrop',
  CONVERTING: 'converting',
  ORGANISE: 'organise',
  REVIEW: 'review',
  MARKING: 'marking',
  SUBMIT: 'submit',
  GENERATING: 'generating',
  DONE: 'done',
};

/**
 * The steps that read the extraction run. Not every step: the poll is a timer on
 * the page, and Add-question or Drag-drop have no submissions to watch.
 *
 * MARKING is included because the run's own retry can still be converting behind
 * it — the teacher moves on from Review before the class has finished.
 */
const EXTRACTION_WATCHED_STEPS = new Set([
  STEPS.CONVERTING,
  STEPS.REVIEW,
  STEPS.MARKING,
]);

const isExtractionWatched = (step) => EXTRACTION_WATCHED_STEPS.has(step);

// Same students, same pages, same order — order matters because it is the
// submission order the draft was created with.
const routingKey = (assignments = {}) =>
  Object.entries(assignments)
    .map(([studentId, pageIds]) => `${studentId}:${(pageIds || []).join(',')}`)
    .sort()
    .join('|');

const isSameRouting = (a, b) => routingKey(a) === routingKey(b);

/**
 * Determines if JeddAI feedback should be requested based on the assignment's reviewedBy field.
 * Returns true for JEDDAI and JEDDAI_ASSISTED tasks, false for TEACHER and P2P tasks.
 */
const shouldRequestJeddaiFeedback = (reviewedBy) => {
  if (!reviewedBy) return false;
  return reviewedBy === 'JEDDAI' || reviewedBy === 'JEDDAI_ASSISTED';
};

/**
 * Whether an existing assignment is a class task, as opposed to a My Drafts
 * document. The server names the type; an older record without one is a class
 * task exactly when it is set to classes.
 */
const isClassTaskAssignment = (assignment) => {
  if (!assignment) return false;
  if (assignment.type) return assignment.type === 'SUBMISSION';
  return Array.isArray(assignment.classIds) && assignment.classIds.length > 0;
};

const studentIdOf = (student) => String(student?.id ?? student?.studentId);

const EMPTY_BATCH = { submissionIds: [], routedPageIdsByStudent: {} };

// The status endpoint is the only thing that knows how far along the run is, so
// when it cannot be read there is nothing honest to draw a bar from.
const FEEDBACK_STATUS_UNAVAILABLE =
  "Feedback was requested, but its progress can't be read right now. It keeps generating in the background.";

// Said on the progress screen itself, because the screen is now something a
// teacher is allowed to walk away from.
const FEEDBACK_KEEPS_GENERATING =
  'You can leave this screen — feedback keeps generating in the background.';

const UNSAVED_EDITS_TITLE = 'Your corrections have not been saved';
const UNSAVED_EDITS_MESSAGE =
  'The responses you edited at Review are only saved when you continue. Leaving now loses them.';

const retryLabel = (count) => `Retry ${count} student${count === 1 ? '' : 's'}`;

const WizardFrame = ({ embedded, fullPage, children, settingsModal }) => {
  const fillHeight = fullPage || embedded;
  const wrapperStyle = fillHeight
    ? {
        padding: 0,
        background: 'transparent',
        minHeight: 0,
        height: '100%',
        flex: 1,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
      }
    : {
        padding: 24,
        background: '#f2f1f3',
        minHeight: '100vh',
        boxSizing: 'border-box',
      };

  return (
    <>
      <div style={wrapperStyle}>
        <div
          style={{
            width: '100%',
            maxWidth: fillHeight ? 'none' : 1600,
            margin: '0 auto',
            flex: fillHeight ? 1 : undefined,
            display: fillHeight ? 'flex' : undefined,
            flexDirection: fillHeight ? 'column' : undefined,
            minHeight: fillHeight ? 0 : undefined,
            height: fillHeight ? '100%' : undefined,
          }}
        >
          {children}
        </div>
      </div>
      {settingsModal}
    </>
  );
};

const DoneCard = ({
  fileCount,
  label,
  students = [],
  assignmentId,
  submission,
  onExit,
}) => {
  const [showShareDownload, setShowShareDownload] = useState(false);
  const [showSubmissions, setShowSubmissions] = useState(false);

  if (showSubmissions && assignmentId) {
    return (
      <div
        style={{
          background: '#fff',
          borderRadius: 16,
          border: '1px solid rgba(201, 198, 204, 0.5)',
          overflow: 'hidden',
        }}
      >
        <DraftSubmissionsView
          assignmentId={assignmentId}
          assignmentTitle={
            submission?.assignment?.displayTitle?.trim() ||
            submission?.assignment?.title ||
            submission?.title
          }
        />
        {onExit && (
          <div style={{ padding: '16px 24px', borderTop: '1px solid rgba(201, 198, 204, 0.3)' }}>
            <button
              type="button"
              onClick={onExit}
              style={{
                height: 40,
                padding: '0 28px',
                borderRadius: 999,
                border: '1px solid rgba(201,198,204,0.85)',
                background: '#fff',
                color: '#302e32',
                fontWeight: 600,
                fontSize: 14,
                cursor: 'pointer',
                fontFamily: "var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif)",
              }}
            >
              Done
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      style={{
        padding: '24px 28px',
        background: '#fff',
        borderRadius: 16,
        border: '1px solid rgba(201, 198, 204, 0.5)',
        fontFamily: "var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif)",
      }}
    >
      <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 600, color: '#1e252a' }}>
        Bulk upload submitted ✅
      </h3>
      <p style={{ margin: '0 0 20px', color: '#6b7280', fontSize: 14 }}>
        {fileCount} {label} sent for JeddAI feedback.
      </p>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => setShowSubmissions(true)}
          style={{
            height: 40,
            padding: '0 28px',
            borderRadius: 999,
            border: 'none',
            background: '#7200e0',
            color: '#fff',
            fontWeight: 600,
            fontSize: 14,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          View Submissions
        </button>
        <button
          type="button"
          onClick={() => setShowShareDownload(true)}
          style={{
            height: 40,
            padding: '0 28px',
            borderRadius: 999,
            border: '1px solid rgba(201,198,204,0.85)',
            background: '#fff',
            color: '#302e32',
            fontWeight: 600,
            fontSize: 14,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Share / Download
        </button>
        {onExit && (
          <button
            type="button"
            onClick={onExit}
            style={{
              height: 40,
              padding: '0 28px',
              borderRadius: 999,
              border: '1px solid rgba(201,198,204,0.85)',
              background: '#fff',
              color: '#302e32',
              fontWeight: 600,
              fontSize: 14,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Done
          </button>
        )}
      </div>
      <ShareOrDownloadControl
        open={showShareDownload}
        onClose={() => setShowShareDownload(false)}
        submission={submission}
        students={students}
        assignmentId={assignmentId}
        variant="bulk"
      />
    </div>
  );
};

export const SchoolTeacherBulkUploadFlow = ({
  initialStep = STEPS.ENTRY,
  onExit,
  onComplete,
  embedded = false,
  fullPage = false,
  showSettingsFirst = false,
  mode = 'schoolTeacher',
  skipQuestion = false,
  stepsConfig,
  // Whether this flow owns a resumable DRAFT. Adding responses to an already
  // published assignment is not one: it has a real assignmentId but nothing ever
  // reads a snapshot back for it, so writing one would only invent version
  // conflicts in a flow with no second tab.
  persistDraftPages = true,
  assignmentId,
  submission,
  students: studentsProp,
  onDraftCreated,
  // Server state for /drafts/:assignmentId. Everything the wizard used to hold
  // only in memory — pages, routing, which submissions exist, and how far along
  // it is — is rebuilt from the assignment's submissions.
  resume,
  registerLeaveGuard,
  // Mobile only (and only with the mobile flag on): an ASYNC in-app confirm
  // that resolves true to leave. Without it — every desktop caller, and the
  // flag-off build — the leave questions stay on window.confirm, unchanged.
  confirmLeave,
}) => {
  const queryClient = useQueryClient();
  const asyncLeaveConfirm =
    typeof confirmLeave === 'function' && isMobileExperienceFlagOn()
      ? confirmLeave
      : null;
  const isMountedRef = useRef(false);
  const leaveEpochRef = useRef(0);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      leaveEpochRef.current += 1;
    };
  }, []);

  const {
    data: classes,
    isLoadingdata: isLoadingClasses,
    isError: isClassesError,
    resetData: reloadClasses,
  } = useActiveClassData();

  /**
   * JED-1390: a class task holds one response per student, so a student who has
   * already handed one in must not be offered here — whichever entry point built
   * `students`. The Tasks board pre-filtered its list; the feedback page passed
   * every student of every class, and routing pages to one who had already
   * submitted minted a second submission beside the first. The wizard reads the
   * task's own roster and applies the rule itself, so no caller can forget it.
   *
   * Nothing is decided until that read lands: a picker rendered against an
   * unknown roster would offer the very students it exists to hide.
   */
  const { data: existingAssignment } = useAssignmentById(assignmentId, Boolean(assignmentId));
  const isClassTask = Boolean(assignmentId) &&
    isClassTaskAssignment(existingAssignment || submission?.assignment);
  const handedIn = useQuery({
    queryKey: ['assignmentSubmissions', assignmentId],
    queryFn: () => getSubmissionsByAssignmentId(assignmentId, { requireValidResponse: true }),
    enabled: isClassTask,
  });
  const handedInRows = isClassTask && handedIn.isSuccess ? handedIn.data || [] : null;
  const isHandedInLoading = isClassTask && !handedIn.isSuccess && !handedIn.isError;
  const isHandedInBroken = isClassTask && handedIn.isError;
  const withoutHandedIn = useCallback(
    (students) => {
      if (!isClassTask) return students;
      // Unknown yet: offer nobody rather than everybody.
      if (!handedInRows) return [];
      return excludeStudentsWithSubmissions(students, handedInRows);
    },
    [isClassTask, handedInRows],
  );

  /**
   * FE-BU21: a roster that came back partial (`degraded`) or not at all.
   *
   * Organise routes pages BY student, so committing against a roster that is
   * missing students is how they get silently dropped from the run — the pages
   * are routed to nobody and nothing downstream ever mentions it again.
   *
   * A task roster whose handed-in reading failed is broken the same way: the
   * rule that keeps duplicates out cannot be applied to it.
   */
  const isRosterBroken =
    Boolean(classes?.degraded) || Boolean(isClassesError) || isHandedInBroken;
  const assignmentClasses = useMemo(() => {
    if (!assignmentId || !Array.isArray(classes)) return classes || [];
    const classIds =
      submission?.assignment?.classIds ||
      submission?.classIds ||
      [...new Set((studentsProp || []).map((student) => student.classId).filter(Boolean))];
    if (!Array.isArray(classIds) || !classIds.length) return classes;
    const allowed = new Set(classIds.map(String));
    const scoped = classes.filter((clazz) => allowed.has(String(clazz.id)));
    return scoped.length ? scoped : classes;
  }, [assignmentId, classes, submission, studentsProp]);

  const offeredStudents = useMemo(() => {
    const rosterClasses = assignmentId ? assignmentClasses : classes;
    const flattened = flattenClassStudents(rosterClasses);
    if (!Array.isArray(studentsProp) || studentsProp.length === 0) {
      return flattened;
    }

    const classIdByStudentId = new Map(
      flattened.map((student) => [String(student.id), student.classId]),
    );

    const fromProps = studentsProp.map((student) => {
      const id = student.id ?? student.studentId;
      const name =
        student.name ||
        student.studentName ||
        student.firstName ||
        'Student';
      return {
        id,
        name,
        studentName:
          student.studentName || student.name || student.firstName || name,
        classId:
          student.classId || classIdByStudentId.get(String(id)) || undefined,
      };
    });

    return fromProps.length ? fromProps : flattened;
  }, [assignmentClasses, classes, assignmentId, studentsProp]);

  // The roster the right rail and the routing are built from.
  const classStudents = useMemo(
    () => withoutHandedIn(offeredStudents),
    [offeredStudents, withoutHandedIn],
  );

  // The class picker's rosters. Narrowed to the students a caller offered, when
  // it offered any; on a class task the handed-in rule is applied to each class
  // directly, so a class whose every student has submitted lists nobody rather
  // than falling back to everybody.
  const filteredAssignmentClasses = useMemo(() => {
    if (!Array.isArray(assignmentClasses) || assignmentClasses.length === 0) {
      return assignmentClasses;
    }
    const offered = Array.isArray(offeredStudents) && offeredStudents.length > 0
      ? new Set(offeredStudents.map(studentIdOf))
      : null;

    return assignmentClasses.map((clazz) => ({
      ...clazz,
      students: withoutHandedIn(
        (clazz.students || []).filter(
          (student) => !offered || offered.has(studentIdOf(student)),
        ),
      ),
    }));
  }, [assignmentClasses, offeredStudents, withoutHandedIn]);

  // Every student the task's classes know, handed-in or not: the roster an error
  // message looks a name up in has to include the student it is about.
  const rosterForMessages = useMemo(
    () => [...classStudents, ...flattenClassStudents(assignmentClasses)],
    [classStudents, assignmentClasses],
  );

  const wizardStepLabels =
    stepsConfig ||
    (mode === 'independentTeacher'
      ? INDEPENDENT_TEACHER_BULK_UPLOAD_STEPS
      : skipQuestion
        ? TASKS_BULK_UPLOAD_STEPS
        : BULK_UPLOAD_WIZARD_STEPS);
  // When showSettingsFirst is true, start at ENTRY to show the settings modal
  // A resumed draft opens where its data says it is, so the derived step wins
  // over every default below it.
  const resolvedInitial = resume?.step
    ? resume.step
    : showSettingsFirst
      ? STEPS.ENTRY
      : skipQuestion
        ? STEPS.DRAG_DROP
        : embedded
          ? STEPS.QUESTION
          : initialStep;
  const [step, setStep] = useState(resolvedInitial);
  const [showSettings, setShowSettings] = useState(showSettingsFirst);
  // Set by onCreateDraft so the close handler can tell a real creation apart
  // from a dismissal — the modal fires both, in that order.
  const createdDraftRef = React.useRef(false);
  const [selectedYear, setSelectedYear] = useState(resume?.selectedYear || null);
  const [selectedSubject, setSelectedSubject] = useState(resume?.selectedSubject || null);
  const [selectedTaskType, setSelectedTaskType] = useState(resume?.selectedTaskType || null);
  const [draftDetails, setDraftDetails] = useState(resume?.draftSettings || {});
  const [questionForm, setQuestionForm] = useState({
    title: resume?.title || '',
    question: resume?.question || '',
    stimulusText: resume?.stimulusText || '',
    stimulusFiles: resume?.stimulusFiles || [],
  });
  const questionTitleBaselineRef = useRef(
    resume && Object.prototype.hasOwnProperty.call(resume, 'expectedTitle')
      ? resume.expectedTitle : resume?.title ?? null
  );
  const mobileQuestionBaselineRef = useRef(registerLeaveGuard ? JSON.stringify(questionForm) : null);
  const mobileQuestionDirtyRef = useRef(false);
  mobileQuestionDirtyRef.current = Boolean(registerLeaveGuard) && JSON.stringify(questionForm) !== mobileQuestionBaselineRef.current;
  // Rebuilt from the snapshot on resume: the rail, the thumbnail strip and the
  // Continue gate all read this, so an empty one looks like a draft with no work
  // in it even when the pages are right there.
  const [files, setFiles] = useState(resume?.files || []);
  const [pages, setPages] = useState(resume?.pages || []);
  const [studentAssignments, setStudentAssignments] = useState(
    resume?.studentAssignments || {},
  );
  const [organizedPages, setOrganizedPages] = useState(resume?.pages || []);
  const [organizedStudents, setOrganizedStudents] = useState(
    resume?.students || [],
  );
  // The submissions Organise created, and the routing they were created with —
  // one value, because the read side answers positionally against that routing
  // and the two must never be able to disagree.
  const [batch, setBatch] = useState({
    submissionIds: resume?.submissionIds || [],
    routedPageIdsByStudent: resume?.studentAssignments || {},
  });
  const [isCreatingSubmissions, setIsCreatingSubmissions] = useState(false);
  const [batchSaveError, setBatchSaveError] = useState(null);
  const pendingRoutingRef = useRef(null);
  const isCreatingSubmissionsRef = useRef(false);
  const mobileUploadPendingRef = useRef(false);
  const mobileUnsavedTextRef = useRef(false);
  const setMobileUnsavedText = useCallback((dirty) => { mobileUnsavedTextRef.current = dirty; }, []);
  const setMobileUploadPending = useCallback((pending) => { mobileUploadPendingRef.current = pending; }, []);
  /**
   * FE-BU23: what the teacher picked at marking, held above the step that draws
   * it so Back does not unmount the selection.
   *
   * Null until they pick something, which is how the step tells "not chosen yet"
   * apart from "chosen, then cleared" and opens on its own defaults.
   */
  const [markingSelection, setMarkingSelection] = useState(() => ({ courseMaterials: resume?.courseMaterials || [] }));
  const committedRoutingRef = useRef(resume?.studentAssignments || {});
  const replacementRequestRef = useRef(null);
  /**
   * FE-BU15: what the teacher corrected at Review, keyed by studentId.
   *
   * Only genuine edits land here — the editor emits on user input alone — so this
   * is "what they changed", not "what was on screen". Submit persists it to the
   * submissions before the run, because extraction is allowed to fail soft only on
   * the promise that this screen can fix it.
   */
  const [reviewEdits, setReviewEdits] = useState({});
  const [reviewEditsPersisted, setReviewEditsPersisted] = useState(false);
  // Raised when leaving Review would walk out with those corrections.
  const [isConfirmingExit, setIsConfirmingExit] = useState(false);
  const recordReviewEdit = useCallback(
    (studentId, text) => {
      setReviewEditsPersisted(false);
      setReviewEdits((prev) => ({ ...prev, [studentId]: text }));
    },
    [],
  );
  // A My Drafts draft gets its assignment at the Question step, so it appears in
  // the sidebar straight away. Kept apart from the assignmentId prop so it cannot
  // change which completion screen the flow shows.
  const [draftAssignmentId, setDraftAssignmentId] = useState(null);
  // The same fact twice on purpose: the ref guards the POST synchronously, the
  // state holds the Continue button down. See createDraftAssignment.
  const isCreatingDraftRef = useRef(false);
  const [isCreatingDraft, setIsCreatingDraft] = useState(false);
  // One key per upload attempt: stepping Back and Continuing again must reuse the
  // submissions rather than mint a second set. Replaced only when routing changes.
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);
  // Separate key for assignment creation: ensures double-clicks on "Create" button
  // only create one draft, even before the ref-based guard in createDraftAssignment
  // takes effect. Never reset since assignment creation happens once per lifecycle.
  const [assignmentIdempotencyKey] = useState(newIdempotencyKey);

  const activeAssignmentId = assignmentId || draftAssignmentId;

  // Fetch assignment data to check reviewedBy field for conditional feedback submission
  const { data: assignmentData } = useAssignmentById(activeAssignmentId);
  const reviewedBy = assignmentData?.reviewedBy || submission?.assignment?.reviewedBy;
  const isManualTask = ['TEACHER', 'P2P'].includes(reviewedBy);
  /**
   * A class task with more than one answerable question. Only then does Review
   * split the text by question; a single-question task — and every My Drafts
   * document — keeps the editor and the single-answer save exactly as they are.
   */
  const multiQuestion =
    Boolean(skipQuestion) &&
    isMultiQuestionAssignment(assignmentData || submission?.assignment);
  const multiQuestionCount = multiQuestion
    ? answerableQuestionsOf((assignmentData || submission?.assignment)?.questions).length
    : 0;
  // studentId -> the split Review holds for them; kept here so it survives
  // stepping back to Organise and forward again.
  const [questionSplits, setQuestionSplits] = useState({});
  const recordQuestionSplit = useCallback((studentId, next) => {
    setQuestionSplits((current) => ({ ...current, [String(studentId)]: next }));
  }, []);
  const hasReviewMode = isManualTask || shouldRequestJeddaiFeedback(reviewedBy);

  // organizedPages is set alongside pages on every Organise change, so these
  // differ only before Organise has run. One reading beats eight copies of the
  // same ternary drifting apart.
  const currentPages = organizedPages.length > 0 ? organizedPages : pages;
  const currentStudents =
    organizedStudents.length > 0 ? organizedStudents : classStudents;

  /**
   * FE18: the page list is autosaved from here, so a refresh during drag-drop or
   * organise resumes where the teacher left off. Submissions do not exist until
   * the Organise commit, so before that this snapshot is the ONLY thing on the
   * server that references the uploaded files.
   *
   * Wired to the same two values createSubmissionsFor reads, so what gets saved
   * and what gets committed cannot drift apart.
   */
  // "Has this draft ever committed" is monotonic, but batch.submissionIds is not:
  // discardSubmissionsIfRerouted clears it for idempotency-key reasons, which
  // would otherwise re-arm writes to a snapshot the server has already dropped.
  const hasCommittedRef = useRef(false);
  if (batch.submissionIds.length > 0) hasCommittedRef.current = true;

  const { flush: flushDraftPages, ...draftPageSync } = useDraftPageSync({
    enabled: persistDraftPages,
    assignmentId: activeAssignmentId,
    pages: currentPages,
    studentAssignments,
    initialVersion: resume?.snapshotVersion ?? 0,
    students: currentStudents,
    // The batch drops the snapshot server-side; saving after that would put a
    // stale one back.
    isCommitted: hasCommittedRef.current,
    // Both failures are shown by DraftPageSyncStatus below, which the hook owns
    // so the states cannot drift.
    onError: () => {},
  });
  const draftSyncStatus = (
    <DraftPageSyncStatus
      hasConflict={draftPageSync.hasConflict}
      hasSaveError={draftPageSync.hasSaveError}
      onRetry={draftPageSync.retry}
      onDiscard={onExit ? () => { releaseNavigationGuard(); onExit(); } : undefined}
      onReload={() => { releaseNavigationGuard(); window.location.reload(); }}
    />
  );

  /**
   * FE21: thumbnails are not persisted (a data URL per page would dwarf the rest
   * of the snapshot), so a resumed page arrives on the placeholder holding only
   * its url. Anything that fails to render keeps the placeholder — a draft whose
   * previews will not draw is still one the teacher has to be able to submit.
   *
   * Both atoms are written because organizedPages shadows pages once Organise has
   * run, and currentPages selects between them.
   */
  const applyThumbnails = React.useCallback((byPageId) => {
    const withThumbnail = (page) =>
      byPageId[page.id] ? { ...page, thumbnail: byPageId[page.id] } : page;
    setPages((prev) => prev.map(withThumbnail));
    setOrganizedPages((prev) => prev.map(withThumbnail));
  }, []);
  useResumedThumbnails(currentPages, applyThumbnails);

  /**
   * Extraction, for the whole assignment, watched from HERE rather than from the
   * Converting step.
   *
   * Converting waits for every current response to settle. Keeping the run here
   * preserves its status across step changes and supplies Review with the same
   * response text and extraction locks.
   */
  const extraction = useAssignmentExtractionRun(activeAssignmentId, {
    enabled: isExtractionWatched(step) && !isCreatingSubmissions,
    submissionIds: batch.submissionIds,
  });

  /**
   * Both atoms are written for the same reason applyThumbnails writes both:
   * organizedPages shadows pages once Organise has run, and currentPages selects
   * between them.
   */
  const applyExtractedText = React.useCallback(
    (submissions) => {
      const routing = batch.routedPageIdsByStudent;
      const withText = (prev) => mapSubmissionsOntoPages(prev, routing, submissions);
      setPages(withText);
      setOrganizedPages(withText);
    },
    [batch.routedPageIdsByStudent],
  );

  const extractionRows = extraction.status?.submissions;
  useEffect(() => {
    if (extractionRows?.length) applyExtractedText(extractionRows);
  }, [extractionRows, applyExtractedText]);

  // ADR-002: Use optimistic submission hook with real backend integration
  const {
    submit,
    submitWithoutFeedback,
    retrySubmit,
    submitError,
    clearSubmitError,
    isSubmitting,
    feedbackRun,
  } = useBulkUploadSubmission({
    pages: currentPages,
    studentAssignments,
    assignmentId: activeAssignmentId,
    submissionIds: batch.submissionIds, // Organise made these; submit must not make more
    resumeMarking: resume?.step === STEPS.GENERATING,
    // Persisted before the run is asked for, so the run marks what the teacher
    // approved rather than what extraction produced.
    reviewEdits,
    students: currentStudents,
    // Save extracted text only for responses the server has finished answering.
    settledStudentIds: extraction.settledStudentIds,
    // Multi-question tasks send one answer per question instead of review edits.
    questionSplits,
    multiQuestion,
    // Fires after the whole batch is ready and its first response is prefetched.
    onComplete: (response) => {
      if (!isMountedRef.current) return;
      setReviewEditsPersisted(true);
      releaseNavigationGuard();
      // If assignmentId exists OR fullPage mode, navigate immediately (don't show DONE screen)
      if (assignmentId || fullPage) {
        onComplete?.(response);
      } else {
        // For standalone drafts flow, show done screen
        setStep(STEPS.DONE);
        onComplete?.(response);
      }
    },
  });

  /**
   * FE-BU03: work this tab is holding that the server does not have.
   *
   * Autosave only backs the page list once the server acknowledges it. Protect
   * the debounce, in-flight and failed-save windows as well as flows that cannot
   * write a snapshot. Release protection as soon as the latest snapshot is saved.
   */
  const isPageListUnbacked = !persistDraftPages;
  const hasUnsavedWork =
    isPageListUnbacked && currentPages.length > 0 && !hasCommittedRef.current;

  // The step the teacher came from, and the one a refused run has to give back.
  const submitStep = skipQuestion ? STEPS.REVIEW : STEPS.MARKING;

  /**
   * FE-BU21: the teacher's corrections at Review are held here until submit
   * persists them, so leaving before that is the one exit that costs work — and
   * it used to cost it silently.
   *
   * Only up to the run: once submit has sent them, the answers on the server are
   * the teacher's own text and there is nothing left in this tab to warn about.
   */
  const holdsUnsavedReviewEdits =
    (hasReviewEdits(reviewEdits) ||
      (multiQuestion && Object.values(questionSplits).some((split) => split?.dirty))) &&
    !reviewEditsPersisted;
  const releaseNavigationGuard = useUnsavedWorkGuard(
    !registerLeaveGuard && (hasUnsavedWork || draftPageSync.hasUnsavedChanges || holdsUnsavedReviewEdits ||
    (skipQuestion && isManualTask && isSubmitting)),
  );

  // Mobile owns an asynchronous route transaction. Opt in to that controller
  // so browser Back also flushes the pending snapshot before unmounting. The
  // desktop flow continues to use its existing route guard above.
  useEffect(() => {
    if (!registerLeaveGuard) return undefined;
    const warnBeforeUnload = (event) => {
      if (!(hasUnsavedWork || holdsUnsavedReviewEdits || isSubmitting ||
        isCreatingSubmissionsRef.current || isCreatingDraftRef.current || mobileUploadPendingRef.current || mobileUnsavedTextRef.current || mobileQuestionDirtyRef.current || draftPageSync.hasConflict || draftPageSync.hasPending?.())) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    const unregister = registerLeaveGuard(async () => {
      if (isSubmitting || isCreatingSubmissionsRef.current || isCreatingDraftRef.current || mobileUploadPendingRef.current) {
        toast(<Toast message="Please wait for the current upload to finish." />);
        return false;
      }
      if ((hasUnsavedWork || holdsUnsavedReviewEdits || mobileUnsavedTextRef.current || mobileQuestionDirtyRef.current) &&
        !(asyncLeaveConfirm
          ? await asyncLeaveConfirm('Your upload has unsaved changes. Leave and lose them?')
          : window.confirm('Your upload has unsaved changes. Leave and lose them?'))) return false;
      try {
        await flushDraftPages?.();
        leaveEpochRef.current += 1;
        return true;
      } catch (error) {
        toast(<Toast message="Your changes could not be saved. Please retry before leaving." />);
        return false;
      }
    });
    return () => { unregister?.(); window.removeEventListener('beforeunload', warnBeforeUnload); };
  }, [registerLeaveGuard, isSubmitting, hasUnsavedWork, holdsUnsavedReviewEdits, flushDraftPages, draftPageSync.hasPending, draftPageSync.hasConflict, asyncLeaveConfirm]);

  const leaveWizard = async () => {
    setIsConfirmingExit(false);
    // Finish the active write and the latest queued snapshot before unmounting.
    // A failed save keeps the wizard open so the teacher can retry it.
    try {
      await flushDraftPages?.();
    } catch (error) {
      toast(<Toast message="Your changes could not be saved. Please retry before closing." />);
      return;
    }
    leaveEpochRef.current += 1;
    setReviewEdits({});
    releaseNavigationGuard();
    if (onExit) {
      onExit();
      return;
    }
    setStep(STEPS.ENTRY);
  };

  // Mobile + flag on: the same questions, asked in-app and awaited. The
  // pending checks run again after each answer — an upload can start while the
  // sheet is open.
  const exitWizardAfterAsyncConfirm = async () => {
    if (registerLeaveGuard && mobileUnsavedTextRef.current &&
      !(await asyncLeaveConfirm('Your text has not been added. Leave and lose it?'))) return;
    if (registerLeaveGuard && mobileQuestionDirtyRef.current &&
      !(await asyncLeaveConfirm('Your question has unsaved changes. Leave and lose them?'))) return;
    if (registerLeaveGuard && (mobileUploadPendingRef.current || isCreatingDraftRef.current)) return;
    if (isSubmitting || isCreatingSubmissionsRef.current) return;
    if (holdsUnsavedReviewEdits) {
      setIsConfirmingExit(true);
      return;
    }
    leaveWizard();
  };

  const exitWizard = () => {
    if (registerLeaveGuard && (mobileUploadPendingRef.current || isCreatingDraftRef.current)) return;
    if (asyncLeaveConfirm) {
      exitWizardAfterAsyncConfirm();
      return;
    }
    if (registerLeaveGuard && mobileUnsavedTextRef.current && !window.confirm('Your text has not been added. Leave and lose it?')) return;
    if (registerLeaveGuard && mobileQuestionDirtyRef.current && !window.confirm('Your question has unsaved changes. Leave and lose them?')) return;
    if (isSubmitting || isCreatingSubmissionsRef.current) return;
    if (holdsUnsavedReviewEdits) {
      setIsConfirmingExit(true);
      return;
    }
    leaveWizard();
  };

  const unsavedEditsPrompt = isConfirmingExit ? (
    <GeneralPopup
      icon={warningIcon}
      title={UNSAVED_EDITS_TITLE}
      textContent={UNSAVED_EDITS_MESSAGE}
      buttonText="Leave and lose them"
      confirmButtonAction={leaveWizard}
      closeBtnText="Keep editing"
      cancelPopup={() => setIsConfirmingExit(false)}
      hidePopup={() => setIsConfirmingExit(false)}
    />
  ) : null;

  const leaveGenerating = () => {
    clearSubmitError();
    setStep(submitStep);
  };

  // What the teacher chose at marking, so a retry replays it rather than making
  // them walk back and rebuild a selection they already made.
  const lastMarkingRef = useRef({ selection: null, controls: null });

  // Where a run that cannot finish, or cannot be read, lets go of the teacher.
  const escapeLabel = skipQuestion ? 'Back to task' : 'Go to drafts';

  const reportRetryRefused = () =>
    toast(<Toast message="That retry could not be sent. Please try again." />);

  /**
   * FE-BU19: a run that stopped short goes back only for the students it failed
   * on — the endpoint takes a submissionIds subset, so the ones already marked
   * are not marked again and their feedback is not overwritten.
   */
  const retryFailedStudents = () => {
    const { selection, controls } = lastMarkingRef.current;
    const submissionIds = feedbackRun.failedSubmissionIds;
    feedbackRun.request(selection, controls, { submissionIds, newRun: true }).catch(reportRetryRefused);
  };

  /**
   * FE-BU17: a refused run is a state to be acted on, not a bar that never moves.
   *
   * The progress screen is reached before the accept comes back — the fan-out runs
   * inline, so waiting for the receipt strands a teacher whose class is already
   * being marked — which means a late rejection lands HERE, on a screen that used
   * to have no way off it but a browser reload.
   */
  const runFailedActions = (
    <>
      <ProgressAction type="button" onClick={retrySubmit} $primary>
        Try again
      </ProgressAction>
      <ProgressAction type="button" onClick={leaveGenerating}>
        Back to {skipQuestion ? 'review' : 'marking'}
      </ProgressAction>
    </>
  );

  /**
   * FE-BU19: the same mechanism, for a run that WAS accepted and then stopped
   * short. A failed run used to change the title and stand there — no retry, and
   * no exit, because the handoff can never fire with nothing readable to open.
   */
  const runIncompleteActions = (
    <>
      <ProgressAction type="button" onClick={retryFailedStudents} $primary>
        {retryLabel(feedbackRun.failedSubmissionIds.length)}
      </ProgressAction>
      <ProgressAction type="button" onClick={() => exitWizard()}>
        {escapeLabel}
      </ProgressAction>
    </>
  );

  /**
   * FE-BU18: the run was accepted; it is the READING of it that failed. So the
   * offers are both about the reading — ask again, or leave and look at the
   * feedback the screen has just promised is still being generated.
   */
  const statusUnreadableActions = (
    <>
      <ProgressAction type="button" onClick={() => feedbackRun.refetchStatus()} $primary>
        Check again
      </ProgressAction>
      <ProgressAction type="button" onClick={() => exitWizard()}>
        {escapeLabel}
      </ProgressAction>
    </>
  );

  /**
   * FE-BU22: a run that is merely SLOW was the last dead end on this screen.
   *
   * Every offer below it answers a run that went wrong; a healthy one got
   * nothing, so a teacher watching thirty students convert had no control at all
   * — and in the full-page entries no wizard chrome around it either, which left
   * the browser's back button as the way out of a screen nothing was wrong with.
   *
   * Leaving costs nothing here: the submissions are committed and the fan-out is
   * server-side, so the run neither needs nor notices this tab.
   */
  const runInFlightActions = (
    <ProgressAction type="button" onClick={() => exitWizard()}>
      {escapeLabel}
    </ProgressAction>
  );

  // Accepted, still working, and nothing wrong with it. One reading, because the
  // way off and the line explaining it must not be able to disagree.
  const isRunInFlight =
    !submitError && !feedbackRun.statusError &&
    (feedbackRun.isRequesting || bulkFeedbackPhase(feedbackRun.status) === 'generating');
  const runPhase = bulkFeedbackPhase(feedbackRun.status);
  const needsContinuation = !isRunInFlight && ['incomplete', 'converting'].includes(runPhase);
  const continueRemainingActions = (
    <>
      <ProgressAction type="button" onClick={() => { clearSubmitError(); setStep(STEPS.REVIEW); }} $primary>
        Continue remaining responses
      </ProgressAction>
      <ProgressAction type="button" onClick={() => exitWizard()}>{escapeLabel}</ProgressAction>
    </>
  );

  const generatingActions = () => {
    if (submitError) return runFailedActions;
    if (feedbackRun.statusError) return statusUnreadableActions;
    if (runPhase === 'unknown' && !feedbackRun.isRequesting) return statusUnreadableActions;
    if (needsContinuation) return continueRemainingActions;
    return feedbackRun.isRecoverable ? runIncompleteActions : runInFlightActions;
  };

  const generatingTitle = () => {
    if (submitError) return 'Feedback could not be requested';
    if (feedbackRun.statusError || runPhase === 'unknown') return 'Checking feedback progress';
    if (needsContinuation) return runPhase === 'converting' ? 'Responses are still being converted' : 'Some responses still need feedback';
    if (!isRunInFlight && runPhase === 'ready') return 'Feedback is ready';
    // Every student failed: there is nothing to open, so stay put and say who and
    // why rather than pushing the teacher at an empty feedback panel.
    if (feedbackRun.isAllFailed) return 'No feedback could be generated';
    return feedbackRun.isRecoverable
      ? 'Some students could not be marked'
      : 'Feedback is being generated';
  };

  const generatingMessage = () => {
    if (submitError) return runRefusalMessage(submitError);
    if (feedbackRun.statusError) return FEEDBACK_STATUS_UNAVAILABLE;
    if (runPhase === 'unknown' && !feedbackRun.isRequesting) return 'Feedback progress could not be confirmed. Check again before continuing.';
    if (needsContinuation) return `${endWithFullStop(feedbackRun.message)} Continue to review the remaining responses before requesting their feedback.`;
    // The counts first, then why the button beside them is safe to press. A
    // finished-but-partial run keeps its own message: nothing is still coming.
    if (!isRunInFlight) return feedbackRun.message;
    return [endWithFullStop(feedbackRun.message), FEEDBACK_KEEPS_GENERATING]
      .filter(Boolean)
      .join(' ');
  };

  const generatingScreen = (
    <FeedbackGenerationProgress
      title={generatingTitle()}
      progress={Math.round(feedbackRun.progress * 100)}
      message={generatingMessage()}
      failures={feedbackRun.failures}
      actions={generatingActions()}
      fullPage
    />
  );

  useEffect(() => {
    // A resumed draft has already been placed by its data; re-running the
    // defaults here would throw it back to the start on every mount.
    if (resume?.step) return;
    if (skipQuestion) {
      setStep(STEPS.DRAG_DROP);
      return;
    }
    // Only auto-navigate to QUESTION if not showing settings first
    if (embedded && !showSettingsFirst) setStep(STEPS.QUESTION);
  }, [embedded, skipQuestion, showSettingsFirst, resume?.step]);

  const goToReview = React.useCallback(() => setStep(STEPS.REVIEW), []);

  /**
   * FE3/FE4: the draft becomes a real row here, at the Question step, so it shows
   * in My Drafts immediately and the wizard has an id to be addressed by.
   *
   * The title is not required — an unnamed draft gets the same placeholder a
   * single draft does, and can be renamed later. Requiring one here would make
   * bulk upload the only flow that refuses to start without a name.
   */
  const draftPayloadFor = (form) => ({
    ...buildDraftAssignmentPayload({
      questionForm: { ...form, title: form.title?.trim() || UNTITLED_DRAFT_TITLE },
      // No page has been routed to anyone yet, so the draft belongs to no class.
      // This is not cosmetic: My Drafts lists a submission-less document through
      // findByTeacherIdAndClassIdsEmptyAndType, so a draft created with the
      // teacher's whole roster attached is invisible in the sidebar until its
      // first submission exists — which is exactly the gap creating the
      // assignment at this step was meant to close.
      students: [],
      studentAssignments: {},
      selectedYear,
      selectedSubject,
      selectedTaskType,
      draftSettings: draftDetails,
    }),
    type: 'DOCUMENT',
  });

  const markCreatingDraft = (value) => {
    isCreatingDraftRef.current = value;
    setIsCreatingDraft(value);
  };

  const postDraftAssignment = async (form) => {
    const payload = draftPayloadFor(form);
    const assignment = await createAssignment(
      payload,
      assignmentIdempotencyKey,
    );
    if (!assignment?.id) throw new Error('Failed to create this draft');
    questionTitleBaselineRef.current = Object.prototype.hasOwnProperty.call(assignment, 'title')
      ? assignment.title : payload.title;
    if (registerLeaveGuard) mobileQuestionBaselineRef.current = JSON.stringify(form);
    mobileQuestionDirtyRef.current = false;
    setDraftAssignmentId(assignment.id);
    onDraftCreated?.(assignment.id);
    return assignment.id;
  };

  /**
   * FE-BU01: one draft per teacher, however fast they click.
   *
   * The in-flight guard is a REF because activeAssignmentId is state: a handler
   * reads the value from the render it was dispatched in, so two clicks in the
   * same tick both saw null, both POSTed, and My Drafts grew a second row with
   * nothing in it. Disabling the button only takes effect a render later, which
   * is a render too late.
   */
  const createDraftAssignment = async (form) => {
    if (isCreatingDraftRef.current) return null;
    markCreatingDraft(true);
    try {
      if (activeAssignmentId) {
        const saved = await saveDraftQuestion(activeAssignmentId, form, queryClient, questionTitleBaselineRef.current);
        questionTitleBaselineRef.current = form.title;
        if (registerLeaveGuard) mobileQuestionBaselineRef.current = JSON.stringify(form);
        mobileQuestionDirtyRef.current = false;
        return saved;
      }
      return await postDraftAssignment(form);
    } finally {
      markCreatingDraft(false);
    }
  };

  const createOrReportDraft = async (form) => {
    try {
      return await createDraftAssignment(form);
    } catch (error) {
      toast(<Toast message={error.message} />);
      return null;
    }
  };

  const continueFromQuestion = async (value) => {
    const nextForm = value ? { ...questionForm, ...value } : questionForm;
    if (value) setQuestionForm(nextForm);
    // No id means a creation this click did not own is already in flight, and
    // Add responses with no assignment id has nothing to save against.
    if (await createOrReportDraft(nextForm)) setStep(STEPS.DRAG_DROP);
  };

  /**
   * FE13: pages become real submissions here, at "Save and continue", carrying the
   * idempotency key so a double-click creates one set rather than two. Extraction
   * runs on them at the next step, which is why they have to exist by now.
   */
  const createSubmissionsForDraft = async () => {
    if (batch.submissionIds.length) return;
    const result = await createSubmissionsFor({
      assignmentId: activeAssignmentId,
      pages: currentPages,
      studentAssignments,
      students: currentStudents,
      idempotencyKey,
    });

    setBatch(result);
    committedRoutingRef.current = studentAssignments;
  };

  // FE-BU10: Accept the final routing as a parameter instead of reading from
  // closure. The Organise step calls onAssignmentsChange and onContinue in the
  // same tick, so reading from state would get the previous render's values.
  const continueFromOrganise = async ({ assignments, pages, students } = {}) => {
    // Held, not just disabled: the routing the batch would carry is only as
    // complete as the roster it was built against.
    if (isCreatingSubmissionsRef.current || isRosterBroken) return;
    isCreatingSubmissionsRef.current = true;
    setIsCreatingSubmissions(true);
    setBatchSaveError(null);
    setStep(STEPS.CONVERTING);
    try {
      // Settle the snapshot first: a debounced save still in flight would land
      // AFTER the batch had deleted it server-side, resurrecting a stale one.
      await flushDraftPages();

      // Replacement is one resumable server operation; the previous responses
      // stay available until the new routing has been saved successfully.
      const finalAssignments = assignments || studentAssignments;
      if (batch.submissionIds.length && isSameRouting(finalAssignments, committedRoutingRef.current)) return;
      const replacing = batch.submissionIds.length > 0;
      const signature = JSON.stringify([batch.submissionIds, finalAssignments]);
      if (replacing && replacementRequestRef.current?.signature !== signature) {
        replacementRequestRef.current = { signature, key: newIdempotencyKey() };
      }
      const requestKey = replacing ? replacementRequestRef.current.key : idempotencyKey;
      pendingRoutingRef.current = { assignments: finalAssignments, pages: pages || currentPages, students: students || currentStudents };

      // Use the passed routing if provided (fresh from Organise), fall back to
      // state for other callers (retry, resume)
      const result = await createSubmissionsFor({
        assignmentId: activeAssignmentId,
        pages: pages || currentPages,
        studentAssignments: assignments || studentAssignments,
        students: students || currentStudents,
        idempotencyKey: requestKey,
        replacesSubmissionIds: replacing ? batch.submissionIds : undefined,
      });

      setBatch(result);
      const previousRouting = committedRoutingRef.current;
      setReviewEdits((current) => Object.fromEntries(Object.entries(current).filter(([studentId]) =>
        isSameRouting({ [studentId]: previousRouting[studentId] }, { [studentId]: finalAssignments[studentId] }))));
      committedRoutingRef.current = finalAssignments;
      if (replacing) {
        const changedPages = new Set([...Object.keys(previousRouting), ...Object.keys(finalAssignments)]
          .filter(id => !isSameRouting({ [id]: previousRouting[id] }, { [id]: finalAssignments[id] }))
          .flatMap(id => [...(previousRouting[id] || []), ...(finalAssignments[id] || [])]));
        const clearOldText = (list) => list.map(page => changedPages.has(page.id) && page.source !== 'manual'
          ? { ...page, extractedText: '', content: '', extractionStatus: 'PENDING', extractionFailureReason: undefined } : page);
        setPages(clearOldText);
        setOrganizedPages(clearOldText);
      }
      replacementRequestRef.current = null;
      pendingRoutingRef.current = null;
      setIdempotencyKey(requestKey);

      setStep(STEPS.CONVERTING);
    } catch (error) {
      if (isFinalBatchRejection(error) || !pendingRoutingRef.current) {
        replacementRequestRef.current = null;
        pendingRoutingRef.current = null;
        setIdempotencyKey(newIdempotencyKey());
        setStep(STEPS.ORGANISE);
      } else {
        // The response may have been lost after the server reserved the plan.
        // Freeze that routing until retry/reload resolves the original operation.
        setBatchSaveError(error);
      }
      toast(<Toast message={submissionBatchErrorMessage(error, rosterForMessages)} />);
    } finally {
      isCreatingSubmissionsRef.current = false;
      setIsCreatingSubmissions(false);
    }
  };

  // Stable callback for DragDrop step to prevent infinite loop
  const handlePagesChange = useCallback((updatedPages) => {
    setPages((prev) => {
      const next =
        typeof updatedPages === 'function'
          ? updatedPages(prev)
          : updatedPages;
      const resolved = Array.isArray(next) ? next : prev;

      // Deduplicate pages by ID to prevent duplicate key errors
      const seen = new Set();
      const deduplicated = resolved.filter((page) => {
        if (seen.has(page.id)) {
          return false;
        }
        seen.add(page.id);
        return true;
      });

      // Removing a file in Add responses also removes its previously routed pages.
      const removed = new Set(prev.filter((page) => !seen.has(page.id)).map((page) => page.id));
      if (removed.size) {
        setStudentAssignments((current) => Object.fromEntries(Object.entries(current).map(
          ([studentId, pageIds]) => [studentId, (pageIds || []).filter((id) => !removed.has(id))]
        )));
      }

      setOrganizedPages(deduplicated);
      return deduplicated;
    });
  }, []);

  // Stable callback for Organise step to prevent infinite loop
  const handleAssignmentsChange = useCallback(
    ({ assignments, pages: updatedPages, students: updatedStudents }) => {
      // Deduplicate pages by ID
      const seen = new Set();
      const deduplicatedPages = updatedPages.filter((page) => {
        if (seen.has(page.id)) {
          return false;
        }
        seen.add(page.id);
        return true;
      });

      // Deduplicate assignments (page IDs in each student's array)
      const deduplicatedAssignments = {};
      Object.keys(assignments).forEach((studentId) => {
        deduplicatedAssignments[studentId] = [...new Set(assignments[studentId] || [])];
      });

      setStudentAssignments(deduplicatedAssignments);
      setOrganizedPages(deduplicatedPages);
      setOrganizedStudents(updatedStudents || []);
      // Also update pages to keep them in sync
      setPages(deduplicatedPages);

      // Sync files: remove files that no longer have any pages
      setFiles((currentFiles) => {
        const remainingFileIds = new Set(
          deduplicatedPages.map((page) => page.fileId).filter(Boolean)
        );
        return currentFiles.filter((file) => remainingFileIds.has(file.id));
      });

    },
    []
  );

  const draftSettings = useMemo(
    () => ({
      ...draftDetails,
      year: selectedYear?.title,
      subject: selectedSubject?.title,
      subjectCode: selectedSubject?.code,
      taskType: selectedTaskType?.title,
      questionText: questionForm.question,
      stimulusText: questionForm.stimulusText,
      stimulusFiles: questionForm.stimulusFiles,
    }),
    [draftDetails, questionForm, selectedYear, selectedSubject, selectedTaskType],
  );

  const buildReviewRows = (assignments, pagesList, studentsList) => {
    const baseStudents = studentsList?.length ? studentsList : classStudents;
    const sourceStudents = mergeStudentRecords(baseStudents, classStudents);

    return sourceStudents.map((student) => {
      const assignedPageIds = assignments[student.id] || [];
      const assignedPages = assignedPageIds
        .map((pageId) => pagesList.find((p) => p.id === pageId))
        .filter(Boolean);

      const pageNumbers = assignedPages
        .map((page) => page.pageNumber)
        .filter(Boolean);

      return {
        id: student.id,
        name: student.name || student.studentName,
        pages: pageNumbers,
        originalPages: assignedPages,
        // What the teacher typed wins over what extraction produced — including
        // when they cleared it, which is why this is `??` and not `||`. Reading it
        // here is also what makes the edit survive stepping back to Review.
        // Extraction state travels on the pages themselves —
        // mapSubmissionsOntoPages stamps it there on every poll tick — so Review
        // reads it off originalPages rather than being handed a second copy.
        responseText: reviewEdits[student.id] ?? responseTextOf(assignedPages),
      };
    });
  };

  const handleSettingsClose = () => {
    setShowSettings(false);
    // PopupDialogueBox calls close() after onCreateDraft too, in the same tick —
    // so `step` is still ENTRY here even when the teacher DID create one. The
    // flag is what separates "created" from "dismissed"; reading `step` alone
    // exited the wizard on the happy path.
    const dismissed = !createdDraftRef.current;
    createdDraftRef.current = false;
    // Dismissing the settings at ENTRY means "not now": without this the panel
    // is left blank with nothing to click.
    if (dismissed && step === STEPS.ENTRY) exitWizard();
  };

  const settingsModal = (
    <PopupDialogueBox
      open={showSettings}
      close={handleSettingsClose}
      mode="create"
      onCreateDraft={(draftSettings) => {
        createdDraftRef.current = true;
        setDraftDetails(draftSettings || {});
        // Extract settings from the draft
        if (draftSettings?.year) setSelectedYear({ title: draftSettings.year });
        if (draftSettings?.subjectCode) setSelectedSubject({
          title: draftSettings.subjectName,
          code: draftSettings.subjectCode
        });
        if (draftSettings?.taskType) setSelectedTaskType({ title: draftSettings.taskType });
        setShowSettings(false);
        // If resuming, go back to the resumed step; otherwise start at QUESTION
        if (!resume?.step || step === STEPS.ENTRY) {
          setStep(STEPS.QUESTION);
        }
        // else: keep current step (resume case)
      }}
    />
  );

  // Said where the routing is done, because that is the only step the roster
  // changes the meaning of.
  const rosterBanner = isRosterBroken ? (
    <TopBannerBox
      bannerMessage={
        isHandedInBroken && !classes?.degraded && !isClassesError
          ? "We couldn't check which students have already submitted this task. Pages can't be saved to students until that loads."
          : "Your class list didn't load fully, so some students may be missing. Pages can't be saved to students until it loads."
      }
      confirmButtonLabel="Try again"
      onConfirm={() => {
        if (isHandedInBroken) handedIn.refetch();
        else reloadClasses?.();
      }}
    />
  ) : null;

  const wizardSteps = new Set([
    STEPS.QUESTION,
    STEPS.DRAG_DROP,
    STEPS.CONVERTING,
    STEPS.ORGANISE,
    STEPS.REVIEW,
    STEPS.MARKING,
    STEPS.GENERATING,
    STEPS.DONE,
  ]);

  const stepNumber = (key) => {
    // Figma: Converting runs after Organise, under Review (My Drafts step 4 / Tasks step 3).
    const map = skipQuestion
      ? {
          [STEPS.DRAG_DROP]: 1,
          [STEPS.ORGANISE]: 2,
          [STEPS.CONVERTING]: 3,
          [STEPS.REVIEW]: 3,
        }
      : {
          [STEPS.QUESTION]: 1,
          [STEPS.DRAG_DROP]: 2,
          [STEPS.ORGANISE]: 3,
          [STEPS.CONVERTING]: 4,
          [STEPS.REVIEW]: 4,
          [STEPS.MARKING]: 5,
        };
    return map[key] || 1;
  };

  const handleStepClick = React.useCallback((stepId) => {
    if (isCreatingSubmissionsRef.current || isSubmitting || batchSaveError) return;
    const stepMap = skipQuestion
      ? {
          1: STEPS.DRAG_DROP,
          2: STEPS.ORGANISE,
          3: STEPS.REVIEW,
        }
      : {
          1: STEPS.QUESTION,
          2: STEPS.DRAG_DROP,
          3: STEPS.ORGANISE,
          4: STEPS.REVIEW,
          5: STEPS.MARKING,
        };

    const targetStep = stepMap[stepId];
    if (targetStep) {
      setStep(targetStep);
    }
  }, [skipQuestion, isSubmitting, batchSaveError]);

  const renderWizardStep = () => {
    if (step === STEPS.GENERATING) {
      return generatingScreen;
    }
    if (step === STEPS.DONE) {
      return (
        <DoneCard
          fileCount={Math.max(files.length, pages.length)}
          label="student responses"
          students={classStudents}
          assignmentId={activeAssignmentId || submission?.assignmentId}
          submission={submission}
          onExit={onExit}
        />
      );
    }
    if (step === STEPS.QUESTION) {
      return (
        <BulkUploadAddQuestionStep
          steps={wizardStepLabels}
          currentStep={stepNumber(STEPS.QUESTION)}
          onStepClick={handleStepClick}
          title={questionForm.title}
          question={questionForm.question}
          stimulusText={questionForm.stimulusText}
          stimulusFiles={questionForm.stimulusFiles}
          onTitleChange={(value) =>
            setQuestionForm((prev) => ({ ...prev, title: value }))
          }
          onQuestionChange={(value) =>
            setQuestionForm((prev) => ({ ...prev, question: value }))
          }
          onStimulusTextChange={(value) =>
            setQuestionForm((prev) => ({ ...prev, stimulusText: value }))
          }
          onStimulusFilesChange={(next) =>
            setQuestionForm((prev) => ({ ...prev, stimulusFiles: next }))
          }
          onBack={exitWizard}
          onClose={exitWizard}
          onContinue={continueFromQuestion}
          isContinueDisabled={isCreatingDraft}
          fullPage={fullPage}
        />
      );
    }
    if (step === STEPS.DRAG_DROP) {
      return (
        <BulkUploadDragDropStep
          steps={wizardStepLabels}
          mode={mode}
          currentStep={stepNumber(STEPS.DRAG_DROP)}
          onStepClick={handleStepClick}
          files={files}
          pages={currentPages}
          onFilesSelect={setFiles}
          onProcessingChange={registerLeaveGuard ? setMobileUploadPending : undefined}
          onUnsavedTextChange={registerLeaveGuard ? setMobileUnsavedText : undefined}
          onPagesChange={handlePagesChange}
          onBack={skipQuestion ? exitWizard : () => setStep(STEPS.QUESTION)}
          onClose={exitWizard}
          onContinue={() => setStep(STEPS.ORGANISE)}
          isContinueDisabled={files.length === 0 && currentPages.length === 0}
          fullPage={fullPage}
        />
      );
    }
    if (step === STEPS.ORGANISE) {
      const isSchoolTeacher = mode === 'schoolTeacher';

      return (
        <>
          {rosterBanner}
          <BulkUploadOrganisePagesStep
            steps={wizardStepLabels}
            currentStep={stepNumber(STEPS.ORGANISE)}
            onStepClick={handleStepClick}
            mode={mode}
            students={currentStudents}
            classes={isSchoolTeacher ? filteredAssignmentClasses || [] : []}
            isLoadingStudents={isSchoolTeacher && (isLoadingClasses || isHandedInLoading)}
            initialPages={currentPages}
            assignments={studentAssignments}
            onBack={() => setStep(STEPS.DRAG_DROP)}
            onClose={exitWizard}
            onContinue={continueFromOrganise}
            isContinueDisabled={isCreatingSubmissions || isRosterBroken}
            onAssignmentsChange={handleAssignmentsChange}
            fullPage={fullPage}
            multiQuestionCount={multiQuestionCount}
          />
        </>
      );
    }
    if (step === STEPS.CONVERTING) {
      return (
        <BulkUploadConvertingStep
          steps={wizardStepLabels}
          currentStep={stepNumber(STEPS.CONVERTING)}
          onStepClick={handleStepClick}
          pages={currentPages}
          assignmentId={activeAssignmentId}
          students={currentStudents}
          extraction={extraction}
          submissionIds={batch.submissionIds}
          isCommitting={isCreatingSubmissions || Boolean(batchSaveError)}
          commitError={batchSaveError}
          onRetryCommit={() => continueFromOrganise(pendingRoutingRef.current || {})}
          onBack={isCreatingSubmissions || batchSaveError ? undefined : () => setStep(STEPS.ORGANISE)}
          onClose={exitWizard}
          onComplete={goToReview}
          fullPage={fullPage}
        />
      );
    }
    if (step === STEPS.REVIEW) {
      // currentPages, not organizedPages: Converting writes its extracted pages to
      // `pages` whenever organizedPages is empty, and reading the raw atom here was
      // the ninth copy of the ternary currentPages exists to be the only one of.
      const reviewRows = buildReviewRows(
        studentAssignments,
        currentPages,
        organizedStudents,
      );
      return (
        <BulkUploadReviewStep
          steps={wizardStepLabels}
          mode={mode}
          currentStep={stepNumber(STEPS.REVIEW)}
          onStepClick={handleStepClick}
          rows={reviewRows}
          assignmentId={assignmentId}
          submissionIds={batch.submissionIds}
          isManualReview={skipQuestion && isManualTask}
          isSubmitting={isSubmitting}
          feedbackStatus={feedbackRun.status}
          isContinueDisabled={isSubmitting || (skipQuestion && !hasReviewMode)}
          submitError={skipQuestion ? submitError?.message : null}
          // Surface any extraction still pending after a resumed status refresh.
          extractionMessage={extraction.stillConverting > 0 ? extraction.message : ''}
          onCheckExtraction={extraction.refetchStatus}
          onBack={() => setStep(STEPS.ORGANISE)}
          onClose={exitWizard}
          onEditResponse={recordReviewEdit}
          multiQuestion={multiQuestion}
          questionSplits={questionSplits}
          onQuestionSplitChange={recordQuestionSplit}
          onContinue={async () => {
            if (!skipQuestion) {
              setStep(STEPS.MARKING);
              return;
            }
            if (!hasReviewMode) return;

            // Check if this task should request JeddAI feedback
            if (isManualTask) {
              const completionEpoch = leaveEpochRef.current;
              if (!await submitWithoutFeedback()) return;
              if (!isMountedRef.current || completionEpoch !== leaveEpochRef.current) return;
              setReviewEditsPersisted(true);
              setReviewEdits({});
              releaseNavigationGuard();
              toast(<Toast message="Responses uploaded successfully" />);
              // Navigate to the assignment or exit
              if (onComplete) {
                onComplete({ assignmentId: activeAssignmentId });
              } else if (onExit) {
                onExit();
              }
              return;
            }

            // Tasks flow submits with no marking selection — but still only leaves
            // this step once the run is actually on its way.
            if (await submit(null, null)) {
              setReviewEditsPersisted(true);
              setQuestionSplits(current => Object.fromEntries(Object.entries(current).map(
                ([studentId, split]) => [studentId, { ...split, dirty: false }],
              )));
              releaseNavigationGuard();
              setStep(STEPS.GENERATING);
            }
          }}
          fullPage={fullPage}
        />
      );
    }
    return (
      <BulkUploadMarkingRubricsStep
        steps={wizardStepLabels}
        currentStep={stepNumber(STEPS.MARKING)}
        onStepClick={handleStepClick}
        draftSettings={draftSettings}
        fileCount={Math.max(files.length, pages.length)}
        selection={markingSelection}
        onSelectionChange={setMarkingSelection}
        onBack={() => setStep(STEPS.REVIEW)}
        onClose={exitWizard}
        // The progress screen is reached only once the run is on its way. Advancing
        // first meant a refused submit left the teacher on a bar at 0% with no back,
        // retry or close — and in fullPage mode, no wizard chrome around it either.
        onSubmit={async (selection, controls) => {
          // Kept so a partial failure can be re-run with the same marking.
          lastMarkingRef.current = { selection, controls };
          if (await submit(selection, controls)) {
            setReviewEditsPersisted(true);
            releaseNavigationGuard();
            setStep(STEPS.GENERATING);
          }
        }}
        submitLabel="Submit now"
        isSubmitting={isSubmitting}
        fullPage={fullPage}
      />
    );
  };

  if (wizardSteps.has(step)) {
    if ((fullPage || !embedded) && step === STEPS.GENERATING) {
      return (
        <>
          {generatingScreen}
          {settingsModal}
        </>
      );
    }
    return (
      <WizardFrame embedded={embedded} fullPage={fullPage} settingsModal={settingsModal}>
        {draftSyncStatus}
        {renderWizardStep()}
        {unsavedEditsPrompt}
      </WizardFrame>
    );
  }

  // ENTRY is the moment before the settings modal is answered. The My Drafts
  // sidebar is already on screen beside this panel, so there is nothing to draw
  // underneath — rendering a second copy of the drafts page here is what used to
  // put an unguarded, crash-prone list behind the modal.
  return <FlowPage>{settingsModal}</FlowPage>;
};

export const IndependentTeacherBulkUploadFlow = (props) => (
  <SchoolTeacherBulkUploadFlow
    mode="independentTeacher"
    stepsConfig={INDEPENDENT_TEACHER_BULK_UPLOAD_STEPS}
    {...props}
  />
);


/**
 * Adding responses to an assignment that already exists.
 *
 * This used to force `persistDraftPages={false}`, on the reading that a published
 * assignment has no draft to persist — so the page list and its per-student
 * routing lived in this tab alone and a refresh lost both, leaving the uploaded
 * files in storage referenced by nothing.
 *
 * The server never had that restriction: /assignments/{id}/draftPages is scoped
 * to "an assignment you teach", with no draft-vs-published check, and the batch
 * commit drops the snapshot the same way either way. So persistence is on by
 * default here too, and the caller that cannot read the snapshot back turns it
 * off — see ResumedTasksFlow.
 */
export const TasksBulkUploadFlow = (props) => (
  <SchoolTeacherBulkUploadFlow
    mode="schoolTeacher"
    skipQuestion
    stepsConfig={TASKS_BULK_UPLOAD_STEPS}
    {...props}
  />
);

export const BulkUploadFlow = SchoolTeacherBulkUploadFlow;
